import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestDetails } from "../../../../api/types/dataCollectionTypes";
import EmailSendDialogIcon from "@entrilia/shared/icons/EmailSendDialogIcon";

interface Props {
  dataRequestId: string;
  submissionIds: string[];
  recipientNames: string[];
  open: boolean;
  onClose: () => void;
  onApply: (dataRequest: DataCollectionRequestDetails) => void;
}

const sendSubmissionReminders = withErrorHandling(adminApi.sendSubmissionReminders);

const SendSubmissionRemindersDialog = ({
  dataRequestId,
  submissionIds,
  recipientNames,
  open,
  onClose,
  onApply,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const handleApply = async () => {
    setIsSaving(true);
    const [dataRequest, error] = await sendSubmissionReminders(dataRequestId, { submissionIds });
    setIsSaving(false);

    if (error) {
      logError(error, "[SendSubmissionRemindersDialog] sendSubmissionReminders");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(`Successfully sent reminder to the recipients of ${recipientNames.length} companies`);
    onApply(dataRequest);
  };

  const description = `Send a reminder to the recipients of ${recipientNames.length === 1 ? recipientNames[0] : recipientNames.length + " portfolio companies"} to submit this request.`;

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Reminder</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={3} sx={{ alignItems: "center" }}>
          <EmailSendDialogIcon sx={{ height: 80, width: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Send reminders
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {description}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={isSaving} onClick={handleApply}>
          Send
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default SendSubmissionRemindersDialog;
